import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { FiX, FiCheckCircle, FiFileText, FiCalendar, FiDollarSign, FiSend, FiRotateCcw } from 'react-icons/fi';
import toast from 'react-hot-toast';

const ParcelasModal = ({ pagamentoId, passageiroId, onClose, onSuccess }) => {
  const [parcelas, setParcelas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [parcelaSelecionada, setParcelaSelecionada] = useState(null);
  const [modalPagar, setModalPagar] = useState(false);
  const [modalReembolso, setModalReembolso] = useState(false);
  const [parcelaReembolso, setParcelaReembolso] = useState(null);
  const [obsReembolso, setObsReembolso] = useState('');
  const [dadosPagamento, setDadosPagamento] = useState({
    valor_pago: '',
    data_pagamento: new Date().toISOString().split('T')[0],
    observacoes: ''
  });

  useEffect(() => {
    fetchParcelas();
  }, [pagamentoId, passageiroId]);

  const fetchParcelas = async () => {
    try {
      setLoading(true);
      const url = pagamentoId 
        ? `/api/parcelas/pagamento/${pagamentoId}`
        : `/api/parcelas/passageiro/${passageiroId}`;
      
      const response = await api.get(url);
      setParcelas(response.data);
    } catch (error) {
      toast.error('Erro ao carregar parcelas');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePagarParcela = (parcela) => {
    setParcelaSelecionada(parcela);
    setDadosPagamento({
      valor_pago: parcela.valor_parcela,
      data_pagamento: new Date().toISOString().split('T')[0],
      observacoes: ''
    });
    setModalPagar(true);
  };

  const handleConfirmarPagamento = async () => {
    if (!parcelaSelecionada) return;

    try {
      await api.post(`/api/parcelas/${parcelaSelecionada.id}/pagar`, dadosPagamento);
      toast.success('Pagamento da parcela registrado com sucesso!');
      setModalPagar(false);
      setParcelaSelecionada(null);
      fetchParcelas();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao registrar pagamento');
      console.error(error);
    }
  };

  const handleGerarRecibo = async (parcela) => {
    if (parcela.status !== 'pago') {
      toast.error('Apenas parcelas pagas podem gerar recibo');
      return;
    }

    try {
      const response = await api.get(`/api/parcelas/${parcela.id}/recibo`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.download = `recibo_parcela_${parcela.numero_parcela}_${parcela.passageiro_nome.replace(/\s/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Recibo gerado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar recibo');
      console.error(error);
    }
  };

  const abrirReembolso = (parcela) => {
    setParcelaReembolso(parcela);
    setObsReembolso('');
    setModalReembolso(true);
  };

  const confirmarReembolso = async () => {
    if (!parcelaReembolso) return;
    try {
      await api.post(`/api/parcelas/${parcelaReembolso.id}/reembolsar`, {
        observacoes: obsReembolso || undefined
      });
      toast.success('Reembolso registrado com sucesso');
      setModalReembolso(false);
      setParcelaReembolso(null);
      fetchParcelas();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao registrar reembolso');
    }
  };

  const handleEnviarReciboWhatsapp = async (parcela) => {
    if (parcela.status !== 'pago') {
      toast.error('Apenas parcelas pagas podem enviar recibo por WhatsApp');
      return;
    }

    try {
      const response = await api.get(`/api/parcelas/${parcela.id}/whatsapp-recibo`);
      const url = response.data?.whatsapp_url;
      if (!url) {
        toast.error('Não foi possível gerar o link do WhatsApp');
        return;
      }
      window.open(url, '_blank');
      toast.success('Link do WhatsApp aberto. Confira e envie o recibo ao cliente.');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao gerar link do WhatsApp');
      console.error(error);
    }
  };

  const handleEnviarLembreteWhatsapp = async (parcela) => {
    if (parcela.status !== 'pendente') {
      toast.error('Lembrete disponível apenas para parcelas pendentes');
      return;
    }

    try {
      const response = await api.post(`/api/parcelas/${parcela.id}/whatsapp-lembrete`);
      const url = response.data?.whatsapp_url;
      if (!url) {
        toast.error('Não foi possível gerar o link de lembrete no WhatsApp');
        return;
      }
      window.open(url, '_blank');
      toast.success('Lembrete aberto no WhatsApp.');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao gerar lembrete no WhatsApp');
      console.error(error);
    }
  };

  const formatarData = (data) => {
    if (!data) return '-';
    const d = new Date(data + 'T00:00:00');
    return d.toLocaleDateString('pt-BR');
  };

  const parcelasPagas = parcelas.filter(p => p.status === 'pago').length;
  const parcelasPendentes = parcelas.filter(p => p.status === 'pendente').length;
  const parcelasReembolsadas = parcelas.filter(p => p.status === 'reembolsado').length;
  const totalParcelas = parcelas.length;
  const valorTotalPago = parcelas
    .filter(p => p.status === 'pago')
    .reduce((sum, p) => sum + parseFloat(p.valor_pago || p.valor_parcela), 0);

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-2xl font-bold text-gray-900">
              Gerenciar Parcelas do Carnê
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <FiX size={24} />
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <div className="p-6">
              {/* Resumo */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Total de Parcelas</p>
                  <p className="text-2xl font-bold text-blue-600">{totalParcelas}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Parcelas Pagas</p>
                  <p className="text-2xl font-bold text-green-600">{parcelasPagas}</p>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Parcelas pendentes</p>
                  <p className="text-2xl font-bold text-yellow-600">{parcelasPendentes}</p>
                </div>
                <div className="bg-rose-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Extorno</p>
                  <p className="text-2xl font-bold text-rose-600">{parcelasReembolsadas}</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Valor em carteira (pagas)</p>
                  <p className="text-2xl font-bold text-purple-600">R$ {valorTotalPago.toFixed(2)}</p>
                </div>
              </div>

              {/* Lista de parcelas */}
              <div className="space-y-2">
                {parcelas.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">
                    Nenhuma parcela encontrada
                  </p>
                ) : (
                  parcelas.map((parcela) => (
                    <div
                      key={parcela.id}
                      className={`border rounded-lg p-4 ${
                        parcela.status === 'pago'
                          ? 'bg-green-50 border-green-200'
                          : parcela.status === 'reembolsado'
                          ? 'bg-rose-50 border-rose-200'
                          : parcela.status === 'cancelado'
                          ? 'bg-gray-50 border-gray-300'
                          : 'bg-white border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div
                            className={`w-12 h-12 rounded-full flex items-center justify-center font-bold ${
                              parcela.status === 'pago'
                                ? 'bg-green-500 text-white'
                                : parcela.status === 'reembolsado'
                                ? 'bg-rose-500 text-white'
                                : parcela.status === 'cancelado'
                                ? 'bg-gray-400 text-white'
                                : 'bg-gray-200 text-gray-600'
                            }`}
                          >
                            {parcela.numero_parcela}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">
                              Parcela {parcela.numero_parcela}
                            </p>
                            <div className="flex gap-4 text-sm text-gray-600 mt-1">
                              <span className="flex items-center gap-1">
                                <FiDollarSign size={14} />
                                R$ {parseFloat(parcela.valor_parcela).toFixed(2)}
                              </span>
                              <span className="flex items-center gap-1">
                                <FiCalendar size={14} />
                                Vencimento: {formatarData(parcela.data_vencimento)}
                              </span>
                              {parcela.status === 'pago' && parcela.data_pagamento && (
                                <span className="flex items-center gap-1 text-green-600">
                                  <FiCheckCircle size={14} />
                                  Pago em: {formatarData(parcela.data_pagamento)}
                                </span>
                              )}
                              {parcela.status === 'reembolsado' && (
                                <span className="text-rose-700 font-medium">
                                  Reembolsado
                                  {parcela.data_reembolso
                                    ? ` (${formatarData(parcela.data_reembolso)})`
                                    : ''}
                                </span>
                              )}
                              {parcela.status === 'cancelado' && (
                                <span className="text-gray-700 font-medium">
                                  Cancelado (desistência)
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {parcela.status === 'pendente' ? (
                            <>
                              <button
                                onClick={() => handlePagarParcela(parcela)}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                              >
                                <FiCheckCircle size={18} />
                                Pagar
                              </button>
                              <button
                                onClick={() => handleEnviarLembreteWhatsapp(parcela)}
                                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2"
                              >
                                <FiSend size={18} />
                                Lembrete
                              </button>
                            </>
                          ) : parcela.status === 'cancelado' ? (
                            <span className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg font-semibold">
                              Cancelado
                            </span>
                          ) : parcela.status === 'reembolsado' ? (
                            <span className="px-4 py-2 bg-rose-100 text-rose-900 rounded-lg font-semibold">
                              Reembolsado
                            </span>
                          ) : (
                            <>
                              <span className="px-4 py-2 bg-green-100 text-green-800 rounded-lg font-semibold flex items-center gap-2">
                                <FiCheckCircle size={18} />
                                Pago
                              </span>
                              <button
                                type="button"
                                onClick={() => abrirReembolso(parcela)}
                                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2"
                                title="Extorno no sistema (valor sai do controle financeiro)"
                              >
                                <FiRotateCcw size={18} />
                                Extorno
                              </button>
                              <button
                                onClick={() => handleGerarRecibo(parcela)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                              >
                                <FiFileText size={18} />
                                Recibo
                              </button>
                              <button
                                onClick={() => handleEnviarReciboWhatsapp(parcela)}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                              >
                                <FiSend size={18} />
                                WhatsApp
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de pagamento */}
      {modalReembolso && parcelaReembolso && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Reembolsar parcela {parcelaReembolso.numero_parcela}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              O valor desta parcela deixa de entrar no Total recebido / estatísticas. O histórico da parcela permanece
              como &quot;reembolsado&quot;.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Observações (opcional)
              </label>
              <textarea
                value={obsReembolso}
                onChange={(e) => setObsReembolso(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                rows={3}
                placeholder="Ex.: PIX devolvido, motivo..."
              />
            </div>
            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={() => {
                  setModalReembolso(false);
                  setParcelaReembolso(null);
                }}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Voltar
              </button>
              <button
                type="button"
                onClick={confirmarReembolso}
                className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
              >
                Confirmar reembolso
              </button>
            </div>
          </div>
        </div>
      )}

      {modalPagar && parcelaSelecionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Registrar Pagamento da Parcela {parcelaSelecionada.numero_parcela}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor da Parcela (R$)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={dadosPagamento.valor_pago}
                  onChange={(e) => setDadosPagamento({ ...dadosPagamento, valor_pago: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data do Pagamento
                </label>
                <input
                  type="date"
                  value={dadosPagamento.data_pagamento}
                  onChange={(e) => setDadosPagamento({ ...dadosPagamento, data_pagamento: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações (opcional)
                </label>
                <textarea
                  value={dadosPagamento.observacoes}
                  onChange={(e) => setDadosPagamento({ ...dadosPagamento, observacoes: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  rows="3"
                />
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={() => {
                  setModalPagar(false);
                  setParcelaSelecionada(null);
                }}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmarPagamento}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Confirmar Pagamento
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ParcelasModal;
